/** The one thing navigation needs to know about an option. */
export interface NavigableOption {
    readonly disabled?: boolean;
}

/** What the pressed key means for the active option. */
export type NavigationAction = 'next' | 'previous' | 'first' | 'last';

/**
 * The index the active option moves to, or the current one when it cannot move.
 *
 * Deliberately WITHOUT wrap-around: at either end the active option stays where it
 * is. That is what the WAI-ARIA listbox pattern prescribes, and the requester asked
 * for it explicitly — a list of reviewers that jumps from the last name back to the
 * first reads as if the key did nothing at all.
 *
 * Disabled options are skipped, never landed on. `-1` means "nothing active yet",
 * both as input and as the answer for a list that holds no selectable option.
 */
export function resolveNextOptionIndex(
    options: readonly NavigableOption[],
    activeIndex: number,
    action: NavigationAction,
): number {
    const isSelectable = (index: number): boolean =>
        index >= 0 && index < options.length && options[index]?.disabled !== true;

    const scan = (from: number, step: number): number | undefined => {
        for (let index = from; index >= 0 && index < options.length; index += step) {
            if (isSelectable(index)) {
                return index;
            }
        }
        return undefined;
    };

    switch (action) {
        case 'first':
            return scan(0, 1) ?? -1;
        case 'last':
            return scan(options.length - 1, -1) ?? -1;
        case 'next':
            return scan(activeIndex + 1, 1) ?? activeIndex;
        case 'previous':
            // From "nothing active" a Backwards key behaves like Last, which is what a
            // user pressing ArrowUp on a freshly opened list expects.
            return activeIndex < 0 ? (scan(options.length - 1, -1) ?? -1) : (scan(activeIndex - 1, -1) ?? activeIndex);
    }
}
